import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input, Button, Typography, Checkbox } from '@crisellevaldez/speculo-ui';
import {
  Download,
  RotateCw,
  Plus,
  Trash,
  UserPlus,
  Upload,
  MoreVertical,
  X,
  ChevronDown,
} from 'lucide-react';

// Mirrors the list in FilterPanel.tsx so the dropdown stays in sync.
// 'Conversation' is the DB value but displays as 'Verified'.
const CONTACT_OUTCOMES = [
  { value: 'Alert', label: 'Alert' },
  { value: 'Active', label: 'Active' },
  { value: 'Inbound', label: 'Inbound' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Conversation', label: 'Verified' },
  { value: 'Follow_Up', label: 'Follow Up' },
  { value: 'Nurture', label: 'Nurture' },
  { value: 'Not_Interested', label: 'Not Interested' },
  { value: 'No_Answer', label: 'No Answer' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'No_Contact', label: 'No Contact' },
  { value: 'Do_Not_Call', label: 'Do Not Call' },
  { value: 'Unclear', label: 'Unclear' },
];
import { ContactTableHeaderProps } from '../../../types/contact.types';
import { EMPTY_FILTER_STATE, type FilterState } from './filterState';
import { countActiveConditions } from '../../../types/contactFilter.types';
import { BulkDeleteModal } from './BulkDeleteModal';
import { BulkAssignModal } from './BulkAssignModal';
import { useFlywheelEnabled } from '../../../stores/useOrganizationStore';
import { FilterButton } from '../../common/FilterButton';

/**
 * The filters live on the page now, not here: the panel that edits them sits
 * beside the table, so it cannot be a child of this header.
 */
type HeaderProps = Omit<ContactTableHeaderProps, 'onFilter'> & {
  filters: FilterState;
  onFilter: (filters: FilterState) => void;
  onToggleFilterPanel: () => void;
  isFilterPanelOpen: boolean;
  /**
   * The table itself. It is a child rather than a sibling so it can share a
   * row with `sidePanel`: the panel has to start level with the search input,
   * which means the split begins below the title row that lives here.
   */
  children?: React.ReactNode;
  /** Rendered beside the search row and the table, narrowing both when open. */
  sidePanel?: React.ReactNode;
};

export const ContactTableHeader: React.FC<HeaderProps> = ({
  searchQuery,
  onSearchChange,
  onRefresh,
  onDownload,
  onAddContact,
  onUploadDatabase,
  onFilter,
  isLoading,
  filters,
  onToggleFilterPanel,
  isFilterPanelOpen,
  children,
  sidePanel,
}) => {
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [openQuickFilter, setOpenQuickFilter] = useState<null | 'lastCall' | 'outcome'>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const quickFiltersRef = useRef<HTMLDivElement>(null);
  const flywheelEnabled = useFlywheelEnabled();

  useEffect(() => {
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  useEffect(() => {
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      if (!quickFiltersRef.current) return;
      if (quickFiltersRef.current.contains(e.target as Node)) return;
      // DualDateRangePicker portals its calendar to document.body, so its
      // clicks fall outside our ref. The calendar root uses `.bg-popover`;
      // treat any click within that as "still inside" the dropdown.
      if ((e.target as HTMLElement).closest('.bg-popover')) return;
      setOpenQuickFilter(null);
    };
    if (openQuickFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openQuickFilter]);

  // Counts only filters surfaced inside the Advanced Filter modal.
  // Quick filters (Last Call dateRange, Outcome) are excluded here so the
  // modal's button doesn't claim credit for filters set elsewhere.
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.selectedTags.length > 0) count++;
    if (filters.leadSource.length > 0) count++;
    if (filters.leadIntent && filters.leadIntent.length > 0) count++;
    if (filters.has_ealerts_registered === true) count++;
    if (flywheelEnabled && filters.flywheelOptOut === false) count++;
    if (filters.aiPaused === true) count++;
    if (filters.flowIds && filters.flowIds.length > 0) count++;
    if (filters.flowTypes && filters.flowTypes.length > 0) count++;
    // Each channel filtered independently counts once.
    if (filters.voiceConsent) count++;
    // SMS consent is hidden for non-flywheel orgs, so a stale value must not
    // show up in the badge as a filter the user cannot see or clear.
    if (flywheelEnabled && filters.smsConsent) count++;
    if (filters.emailConsent) count++;
    // Custom conditions count individually — one badge for the whole builder
    // would understate a filter carrying several comparisons.
    count += countActiveConditions(filters.advancedFilter);
    return count;
  };

  const handleReset = () => {
    onFilter(EMPTY_FILTER_STATE);
    onSearchChange('', '');
  };

  const handleRefresh = () => {
    handleReset();
    onRefresh();
  };

  const removeFilter = (key: string) => {
    const updated = { ...filters };
    switch (key) {
      case 'tags':
        updated.selectedTags = [];
        updated.matchAllTags = false;
        break;
      case 'outcome':
        updated.outcome = [];
        break;
      case 'leadSource':
        updated.leadSource = [];
        break;
      case 'leadIntent':
        updated.leadIntent = [];
        break;
      case 'dateRange':
        updated.dateRange = { startDate: null, endDate: null };
        break;
      case 'ealerts':
        updated.has_ealerts_registered = null;
        break;
      case 'flywheel':
        updated.flywheelOptOut = null;
        break;
      case 'aiPaused':
        updated.aiPaused = null;
        break;
      case 'flowIds':
        updated.flowIds = [];
        break;
      case 'flowTypes':
        updated.flowTypes = [];
        break;
    }
    onFilter(updated);
    onFilter(updated);
  };

  const filterChips = useMemo(() => {
    const chips: { key: string; label: string }[] = [];
    if (filters.selectedTags.length > 0) {
      const joiner = filters.matchAllTags && filters.selectedTags.length > 1 ? ' AND ' : ', ';
      chips.push({ key: 'tags', label: `Tags: ${filters.selectedTags.join(joiner)}` });
    }
    if (filters.outcome.length > 0) {
      chips.push({ key: 'outcome', label: `Outcome: ${filters.outcome.join(', ')}` });
    }
    if (filters.leadSource.length > 0) {
      chips.push({ key: 'leadSource', label: `Source: ${filters.leadSource.join(', ')}` });
    }
    if (filters.leadIntent && filters.leadIntent.length > 0) {
      chips.push({ key: 'leadIntent', label: `Intent: ${filters.leadIntent.join(', ')}` });
    }
    if (filters.dateRange.startDate && filters.dateRange.endDate) {
      const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      chips.push({
        key: 'dateRange',
        label: `Date: ${fmt(filters.dateRange.startDate)} – ${fmt(filters.dateRange.endDate)}`,
      });
    }
    if (filters.has_ealerts_registered === true) {
      chips.push({ key: 'ealerts', label: 'E-Alerts: Registered' });
    }
    if (flywheelEnabled && filters.flywheelOptOut === false) {
      chips.push({ key: 'flywheel', label: 'In Follow-up Flywheel' });
    }
    if (filters.aiPaused === true) {
      chips.push({ key: 'aiPaused', label: 'AI Paused' });
    }
    if (filters.flowIds && filters.flowIds.length > 0) {
      chips.push({
        key: 'flowIds',
        label: `Flow${filters.flowIds.length > 1 ? 's' : ''}: ${filters.flowIds.length} selected`,
      });
    }
    if (filters.flowTypes && filters.flowTypes.length > 0) {
      chips.push({
        key: 'flowTypes',
        label: `Flow Type: ${filters.flowTypes.join(', ')}`,
      });
    }
    return chips;
  }, [filters, flywheelEnabled]);

  const filterCount = getActiveFilterCount();

  // Quick-filter toggles for the dropdown popovers.
  const toggleOutcome = (value: string) => {
    const current = filters.outcome;
    const updated = {
      ...filters,
      outcome: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    };
    onFilter(updated);
    onFilter(updated);
  };

  const outcomeCount = filters.outcome.length;

  return (
    <>
      <div className="space-y-4">
        {/* Row 1: Title + Actions */}
        <div className="flex items-center justify-between">
          <Typography variant="h3" as="h1" className="!text-xl font-bold">
            Contacts
          </Typography>
          <div className="flex items-center gap-2">
            <div className="relative" ref={moreMenuRef}>
              <Button
                variant="outline"
                size="sm"
                className="bg-white dark:bg-transparent"
                onClick={() => setShowMoreMenu(!showMoreMenu)}
              >
                <MoreVertical className="w-4 h-4" />
                <span className="hidden sm:inline">Actions</span>
              </Button>
              {showMoreMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border shadow-lg contacts-dropdown z-50 py-1">
                  {flywheelEnabled && (
                    <button
                      onClick={() => {
                        setIsBulkAssignModalOpen(true);
                        setShowMoreMenu(false);
                      }}
                      className="w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" /> Bulk Claim
                    </button>
                  )}
                  {onUploadDatabase && (
                    <button
                      onClick={() => {
                        onUploadDatabase();
                        setShowMoreMenu(false);
                      }}
                      className="w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" /> Upload Database
                    </button>
                  )}
                  <button
                    onClick={() => {
                      onDownload();
                      setShowMoreMenu(false);
                    }}
                    className="w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" /> Download CSV
                  </button>
                  <div className="border-t border-border my-1" />
                  <button
                    onClick={() => {
                      setIsBulkDeleteModalOpen(true);
                      setShowMoreMenu(false);
                    }}
                    className="w-full px-3 py-2 text-sm text-left text-red-500 hover:bg-accent flex items-center gap-2"
                  >
                    <Trash className="w-4 h-4" /> Bulk Delete
                  </button>
                </div>
              )}
            </div>
            <Button size="sm" onClick={onAddContact}>
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Contact</span>
            </Button>
          </div>
        </div>

        {/* Search row, chips and table share a column so the panel beside them
            starts level with the search input rather than the title above. */}
        {/* No gap here: a flex gap still applies to a zero-width child, so a
            closed panel left a strip of dead space beside the table. The panel
            adds its own left margin when open instead. */}
        <div className="flex flex-col lg:flex-row">
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {/* Row 2: Search + Quick Filters + Filter toolbar */}
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
              <div className="flex-1 min-w-0">
                <Input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value, '')}
                  className="w-full"
                />
              </div>
              {/* Outcome + Filter + Refresh share a single row on every
              breakpoint so on mobile they all sit beside each other
              under the search instead of stacking onto three rows. */}
              <div className="flex items-center gap-2 flex-wrap">
                <div ref={quickFiltersRef} className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    className={`gap-1.5 bg-white dark:bg-transparent h-9${
                      outcomeCount > 0
                        ? '!bg-amber-100 dark:!bg-amber-500/20 !text-amber-800 dark:!text-amber-200 !border-amber-300 dark:!border-amber-500/40'
                        : ''
                    }`}
                    onClick={() =>
                      setOpenQuickFilter(openQuickFilter === 'outcome' ? null : 'outcome')
                    }
                  >
                    Outcome
                    {outcomeCount > 0 && ` (${outcomeCount})`}
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                  {openQuickFilter === 'outcome' && (
                    <div className="absolute top-full right-0 mt-1 z-50 bg-card border border-border rounded-lg shadow-lg py-1 w-52 max-h-80 overflow-y-auto">
                      {CONTACT_OUTCOMES.map((o) => (
                        <label
                          key={o.value}
                          className="flex items-center gap-2 px-3 py-1.5 hover:bg-accent cursor-pointer"
                        >
                          <Checkbox
                            checked={filters.outcome.includes(o.value)}
                            onChange={() => toggleOutcome(o.value)}
                          />
                          <span className="text-sm text-foreground">{o.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <FilterButton
                  onClick={onToggleFilterPanel}
                  activeCount={filterCount}
                  pressed={isFilterPanelOpen}
                  hideLabelOnMobile
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="h-9"
                >
                  <RotateCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Row 3: Filter chips */}
            {filterChips.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mr-0.5">
                  Filters
                </span>
                {filterChips.map((chip) => (
                  <span
                    key={chip.key}
                    className="filter-chip inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium border border-foreground/10 shadow-sm"
                  >
                    {chip.label}
                    <button
                      onClick={() => removeFilter(chip.key)}
                      aria-label={`Remove ${chip.label} filter`}
                      className="inline-flex items-center justify-center rounded-full p-0.5 text-foreground/50 hover:text-foreground hover:bg-foreground/10 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <button
                  onClick={handleReset}
                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors ml-0.5"
                >
                  <X className="w-3 h-3" />
                  Clear all
                </button>
              </div>
            )}
            {children}
          </div>
          {sidePanel}
        </div>
      </div>

      <BulkDeleteModal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        onSuccess={onRefresh}
      />

      <BulkAssignModal
        isOpen={isBulkAssignModalOpen}
        onClose={() => setIsBulkAssignModalOpen(false)}
        onSuccess={onRefresh}
      />
    </>
  );
};
